/**
 * MapUnitLabel Component
 * Renders a unit label with optional heart icon
 */

import React from 'react';
import { HeartIcon } from '../MapLabels/MapLabelIcons';
import './MapUnitLabel.scss';

/**
 * Custom comparison function for React.memo
 * Checks nested properties that affect rendering (like status abbreviation)
 */
const arePropsEqual = (prevProps, nextProps) => {
  // Check primitive props
  if (prevProps.isFavorited !== nextProps.isFavorited) return false;
  if (prevProps.showHeartEnabled !== nextProps.showHeartEnabled) return false;
  if (prevProps.showOccupancyInLabel !== nextProps.showOccupancyInLabel) return false;
  if (prevProps.activeZoom !== nextProps.activeZoom) return false;
  if (prevProps.baseZoom !== nextProps.baseZoom) return false;

  // Check position
  if (prevProps.position?.x !== nextProps.position?.x) return false;
  if (prevProps.position?.y !== nextProps.position?.y) return false;

  // Check unit properties that affect rendering
  const prevUnit = prevProps.unit;
  const nextUnit = nextProps.unit;
  if (prevUnit?.displayMode !== nextUnit?.displayMode) return false;

  // Check nested info properties
  const prevInfo = prevUnit?.info;
  const nextInfo = nextUnit?.info;
  if (prevInfo?.label_text !== nextInfo?.label_text) return false;
  if (prevInfo?.contrast_text_color !== nextInfo?.contrast_text_color) return false;

  // Check residence_status (this is key for status updates)
  const prevStatus = prevInfo?.residence_status;
  const nextStatus = nextInfo?.residence_status;
  if (prevStatus?.abbreviation !== nextStatus?.abbreviation) return false;
  if (prevStatus?.name !== nextStatus?.name) return false;

  return true;
};

/**
 * Map Unit Label Component - simple text display, no interaction
 * Positioned at label.x, label.y from unit data
 * Memoized with custom comparison to detect nested status changes
 */
const MapUnitLabel = React.memo(({
  unit,
  position,
  isFavorited,
  showHeartEnabled,
  showOccupancyInLabel = false,
  activeZoom,
  baseZoom,
}) => {
  const { info, displayMode } = unit;
  const {
    label_text, residence_status, contrast_text_color, map_building_grouping,
  } = info || {};
  const abbreviation = showOccupancyInLabel ? (residence_status?.abbreviation) : null;

  // Skip if no text to display
  if (!label_text && !abbreviation) return null;

  // Get base font size from map building grouping data (parse in case it's a string like "4px")
  const rawTextSize = map_building_grouping?.text_size;
  const baseFontSize = parseFloat(rawTextSize) || 11;

  // Calculate zoom-based font size: double for each zoom level above base, cap at 20px
  const zoomDiff = Math.max(0, (activeZoom || baseZoom) - baseZoom);
  const scaledSize = baseFontSize * (zoomDiff + 1);
  const fontSize = Math.min(scaledSize, 20);

  // Determine text color based on displayMode
  // Active: use pre-computed contrast color from API
  // Inactive: stroke color (dark) - handled by CSS
  const isActive = displayMode === 'active';
  const textColor = isActive ? contrast_text_color : undefined;

  // Show heart only when favoriting is enabled and this unit is favorited
  const showHeart = showHeartEnabled && isFavorited;

  return (
    <div
      className={`htmlUnitLabel ${isActive ? 'htmlUnitLabel--active' : 'htmlUnitLabel--inactive'}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        fontSize: `${fontSize}px`,
        ...(textColor && { '--unit-text-color': textColor }),
      }}
    >
      {showHeart && <HeartIcon className="htmlUnitLabel__heart" isActive={isActive} />}
      {label_text && <span className="htmlUnitLabel__text">{label_text}</span>}
      {abbreviation && <span className="htmlUnitLabel__abbr">{abbreviation}</span>}
    </div>
  );
}, arePropsEqual);

export default MapUnitLabel;
